export type HeaderCheck =
  | 'spf_result'
  | 'spf_alignment'
  | 'dkim_result'
  | 'dkim_alignment'
  | 'dmarc_result'
  | 'dmarc_compliance'
  | 'arc_result';

export type ConditionNode = {
  id: string;
  node_type: 'condition';
  check: HeaderCheck | null;
};

export type ChildConditionGroup = {
  id: string;
  node_type: 'group';
  operator: 'all' | 'any';
  children: ConditionNode[];
};

export type ConditionGroup = {
  id: string;
  node_type: 'group';
  operator: 'all' | 'any';
  children: ChildConditionGroup[];
};

export const CHECK_DEFINITIONS: Record<HeaderCheck, {protocol: string; type: string}> = {
  spf_result: {protocol: 'SPF', type: 'Authentication'},
  spf_alignment: {protocol: 'SPF', type: 'Alignment'},
  dkim_result: {protocol: 'DKIM', type: 'Authentication'},
  dkim_alignment: {protocol: 'DKIM', type: 'Alignment'},
  dmarc_result: {protocol: 'DMARC', type: 'Authentication'},
  dmarc_compliance: {protocol: 'DMARC', type: 'Compliance'},
  arc_result: {protocol: 'ARC', type: 'Validation'},
};

const HEADER_CHECKS = Object.keys(CHECK_DEFINITIONS) as HeaderCheck[];

let sequence = 0;
export const nodeId = () => `condition-${Date.now()}-${sequence++}`;

export const blankCondition = (): ConditionNode => ({
  id: nodeId(), node_type: 'condition', check: null,
});

export const emptyRoot = (): ConditionGroup => ({
  id: nodeId(), node_type: 'group', operator: 'all', children: [],
});

export class UnsupportedConditionTreeError extends Error {
  constructor() {
    super('This rule contains an unsupported condition structure and cannot be edited.');
    this.name = 'UnsupportedConditionTreeError';
    Object.setPrototypeOf(this, UnsupportedConditionTreeError.prototype);
  }
}

const unsupportedTree = (): never => {
  throw new UnsupportedConditionTreeError();
};

const isHeaderCheck = (value: unknown): value is HeaderCheck => (
  typeof value === 'string' && HEADER_CHECKS.includes(value as HeaderCheck)
);

const hydrateCondition = (condition: any): ConditionNode => {
  if (condition?.node_type !== 'condition' || !isHeaderCheck(condition.check)) {
    return unsupportedTree();
  }
  return {id: nodeId(), node_type: 'condition', check: condition.check};
};

const hydrateChildGroup = (group: any): ChildConditionGroup => {
  if (
    group?.node_type !== 'group'
    || (group.operator !== 'all' && group.operator !== 'any')
    || !Array.isArray(group.children)
    || group.children.length === 0
    || group.children.some((child: any) => child?.node_type === 'group')
  ) {
    return unsupportedTree();
  }
  const children = group.children.map(hydrateCondition);
  const checks = children.map((condition: ConditionNode) => condition.check);
  if (checks.length !== new Set(checks).size) return unsupportedTree();
  return {
    id: nodeId(),
    node_type: 'group',
    operator: group.operator,
    children,
  };
};

const hydrateRoot = (group: any): ConditionGroup => {
  if (
    !group
    || (group.node_type !== undefined && group.node_type !== 'group')
    || (group.operator !== 'all' && group.operator !== 'any')
    || !Array.isArray(group.children)
    || group.children.length === 0
  ) {
    return unsupportedTree();
  }
  const root: ConditionGroup = {
    id: nodeId(),
    node_type: 'group',
    operator: group.operator,
    // Older rules may contain conditions directly beneath the root. The editor
    // uses groups only, so wrap each direct condition in an ordered singleton
    // group. A singleton's operator is immaterial, while the root operator and
    // item order retain the original expression semantics.
    children: group.children.map((child: any) => child?.node_type === 'group'
      ? hydrateChildGroup(child)
      : ({
        id: nodeId(),
        node_type: 'group' as const,
        operator: 'all' as const,
        children: [hydrateCondition(child)],
      })),
  };
  return root;
};

export const conditionTreeFromRule = (rule: any): ConditionGroup => {
  if (rule?.condition_group) return hydrateRoot(rule.condition_group);
  const checks = rule?.checks || [];
  if (!Array.isArray(checks)) return unsupportedTree();
  if (checks.length === 0) return emptyRoot();
  return hydrateRoot({
    node_type: 'group',
    operator: rule?.match_mode || 'all',
    children: checks.map((check: HeaderCheck) => ({node_type: 'condition', check})),
  });
};

export const serializeConditionGroup = (group: ConditionGroup): any => ({
  node_type: 'group',
  operator: group.operator,
  children: group.children.map(child => ({
    node_type: 'group',
    operator: child.operator,
    children: child.children.map(condition => {
      if (!condition.check) return unsupportedTree();
      return {node_type: 'condition', check: condition.check};
    }),
  })),
});

export const allConditions = (group: ConditionGroup): ConditionNode[] => (
  group.children.flatMap(child => child.children)
);

export const allChecks = (group: ConditionGroup): HeaderCheck[] => (
  allConditions(group)
    .map(condition => condition.check)
    .filter((check): check is HeaderCheck => check !== null)
);

export const hasDuplicateCheckInConditionGroup = (
  group: ConditionGroup,
  conditionId: string,
  check: HeaderCheck,
): boolean => {
  const targetGroup = group.children.find(child => (
    child.children.some(condition => condition.id === conditionId)
  ));
  return Boolean(targetGroup?.children.some(condition => (
    condition.id !== conditionId && condition.check === check
  )));
};

export const duplicateConditionGroupIds = (group: ConditionGroup): Set<string> => {
  const groupsBySignature = group.children.reduce((groups, child) => {
    const checks = child.children
      .map(condition => condition.check)
      .filter((check): check is HeaderCheck => check !== null)
      .sort();
    const signature = `${child.operator}:${checks.join(',')}`;
    const matchingGroups = groups.get(signature) || [];
    matchingGroups.push(child.id);
    groups.set(signature, matchingGroups);
    return groups;
  }, new Map<string, string[]>());

  return new Set(
    Array.from(groupsBySignature.values())
      .filter(groupIds => groupIds.length > 1)
      .flat(),
  );
};

export const hasDuplicateConditionGroups = (group: ConditionGroup): boolean => (
  duplicateConditionGroupIds(group).size > 0
);

export const hasBlankConditions = (group: ConditionGroup): boolean => (
  allConditions(group).some(condition => condition.check === null)
);

export const addChildGroup = (group: ConditionGroup): ConditionGroup => ({
  ...group,
  children: [...group.children, {
    id: nodeId(), node_type: 'group', operator: 'all', children: [blankCondition()],
  }],
});

export const addGroupCondition = (group: ConditionGroup, groupId: string): ConditionGroup => ({
  ...group,
  children: group.children.map(child => (
    child.id === groupId
      ? {...child, children: [...child.children, blankCondition()]}
      : child
  )),
});

export const updateGroupOperator = (
  group: ConditionGroup,
  groupId: string,
  operator: 'all' | 'any',
): ConditionGroup => ({
  ...group,
  operator: group.id === groupId ? operator : group.operator,
  children: group.children.map(child => (
    child.id === groupId ? {...child, operator} : child
  )),
});

export const updateCondition = (
  group: ConditionGroup,
  conditionId: string,
  check: HeaderCheck,
): ConditionGroup => ({
  ...group,
  children: group.children.map(child => ({
    ...child,
    children: child.children.map(condition => (
      condition.id === conditionId ? {...condition, check} : condition
    )),
  })),
});

export const removeCondition = (group: ConditionGroup, conditionId: string): ConditionGroup => ({
  ...group,
  children: group.children.reduce<ChildConditionGroup[]>((children, child) => {
    const remaining = child.children.filter(condition => condition.id !== conditionId);
    return remaining.length ? [...children, {...child, children: remaining}] : children;
  }, []),
});
